import {
  EVENT_LANDING_PAGE_VIEW,
  EVENT_SEARCH_PAGE_VIEW,
  EVENT_SEARCH_SUBMITTED,
  EVENT_SEARCH_FILTERS_CHANGED,
  EVENT_SEARCH_SORT_CHANGED,
  EVENT_SEARCH_RESULT_CLICKED,
  EVENT_CREATE_ACCOUNT_CLICKED,
  EVENT_SIGNUP_COMPLETED,
} from './events';

export class LoggingAnalyticsHandler {
  trackPageView(url) {
    console.log('Analytics page view:', url);
  }
  trackEvent(eventName, payload) {
    console.log('Analytics event:', eventName, payload);
  }
}

// Google Analytics 4 (GA4) using gtag.js script, which is included in util/includeScripts.js
export class GoogleAnalyticsHandler {
  trackPageView(canonicalPath, previousPath) {
    // GA4 property. Manually send page_view events
    // https://developers.google.com/analytics/devguides/collection/gtagjs/single-page-applications
    // Note 1: You should turn "Enhanced measurement" off.
    //         It attaches own listeners to elements and that breaks in-app navigation.
    // Note 2: If previousPath is null (just after page load), gtag script sends page_view event automatically.
    //         Only in-app navigation needs to be sent manually from SPA.
    // Note 3: Timeout is needed because gtag script picks up <title>,
    //         and location change event happens before initial rendering.
    if (previousPath && window.gtag) {
      window.setTimeout(() => {
        window.gtag('event', 'page_view', {
          page_path: canonicalPath,
        });
      }, 300);
    }
  }
  trackEvent(eventName, payload) {
    if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
    try {
      window.gtag('event', eventName, payload || {});
    } catch (e) {
      // analytics must never break the app
    }
  }
}

// Meta/Facebook Pixel (fbq) handler
export class FacebookPixelHandler {
  trackPageView(canonicalPath, previousPath) {
    if (previousPath && window.fbq) {
      window.setTimeout(() => {
        window.fbq('track', 'PageView', { page_path: canonicalPath });
      }, 300);
    }
  }
  trackEvent(eventName, payload) {
    if (typeof window === 'undefined' || typeof window.fbq !== 'function') return;
    try {
      // Map our SignupCompleted to Meta's standard CompleteRegistration event.
      if (eventName === EVENT_SIGNUP_COMPLETED) {
        window.fbq('track', 'CompleteRegistration', payload || {});
      } else {
        window.fbq('trackCustom', eventName, payload || {});
      }
    } catch (e) {
      // analytics must never break the app
    }
  }
}

// Reddit Pixel (rdt) handler.
//
// Reddit standard events: PageVisit, ViewContent, Search, AddToCart, AddToWishlist,
// Purchase, Lead, SignUp, Custom. Anything else is sent as Custom with customEventName
// so it still shows up in Reddit Events Manager.
//
// All calls are guarded — if the rdt snippet is blocked, missing, or throws, we no-op
// so analytics never breaks the app.
export class RedditPixelHandler {
  trackPageView(canonicalPath, previousPath) {
    // The pixel auto-fires PageVisit on initial load (see public/static/scripts/reddit/reddit.js).
    // Only fire on in-app navigation to avoid double-counting.
    if (previousPath && this._rdt()) {
      window.setTimeout(() => {
        this._safeTrack('PageVisit', { page_path: canonicalPath });
      }, 300);
    }
  }

  trackEvent(eventName, payload = {}) {
    if (!this._rdt()) return;
    const { redditEvent, redditPayload } = mapToRedditEvent(eventName, payload);
    this._safeTrack(redditEvent, redditPayload);
  }

  _rdt() {
    return typeof window !== 'undefined' && typeof window.rdt === 'function' ? window.rdt : null;
  }

  _safeTrack(redditEvent, redditPayload) {
    try {
      const rdt = this._rdt();
      if (!rdt) return;
      rdt('track', redditEvent, redditPayload);
    } catch (e) {
      // pixel blocked / not ready — never throw out of analytics
    }
  }
}

// Map our internal event names + payloads to the closest Reddit standard event.
// Anything we don't have a standard for is sent as `Custom` with `customEventName`
// preserved so it can be filtered in Reddit Events Manager.
export const mapToRedditEvent = (eventName, payload = {}) => {
  switch (eventName) {
    case EVENT_LANDING_PAGE_VIEW:
      return { redditEvent: 'PageVisit', redditPayload: payload };
    case EVENT_SIGNUP_COMPLETED:
      return { redditEvent: 'SignUp', redditPayload: payload };
    case EVENT_SEARCH_PAGE_VIEW:
    case EVENT_SEARCH_SUBMITTED:
      return { redditEvent: 'Search', redditPayload: payload };
    case EVENT_SEARCH_RESULT_CLICKED:
      return { redditEvent: 'ViewContent', redditPayload: payload };
    case EVENT_SEARCH_FILTERS_CHANGED:
    case EVENT_SEARCH_SORT_CHANGED:
    case EVENT_CREATE_ACCOUNT_CLICKED:
    default:
      return {
        redditEvent: 'Custom',
        redditPayload: { customEventName: eventName, ...payload },
      };
  }
};
